import { graphqlQuery } from '@/lib/api/graphql';
import type { GraphQLConnection } from '@/lib/graphql-pagination';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import { useDashboardFilterUsers } from './useDashboardFilterUsers';
import { useModeAwareQuery } from './useModeAwareQuery';

// --- Types ---

export interface TeamMemberOverview {
  userId: number;
  name: string;
  initials: string;
  avatarColor: string;
  activeCases: number;
  collected: number;
  outstanding: number;
  closed: number;
}

// --- GraphQL ---

interface CaseTotalsResponse {
  collectionCases: {
    collectionCaseTotals: {
      totalCount: number;
      totalOutstanding: number;
      totalDisplayOutstanding: number;
    };
  };
}

interface WeeklyTrendNode {
  creditControllerId: number | null;
  collectedAmount: number;
}

interface WeeklyTrendResponse {
  dashboard: {
    weeklyCollectionTrendList: GraphQLConnection<WeeklyTrendNode>;
  };
}

const CASE_TOTALS_QUERY = `
  query TeamCaseTotals($filter: CollectionCaseListFilterInput) {
    collectionCases {
      collectionCaseTotals(filter: $filter) {
        totalCount
        totalOutstanding
        totalDisplayOutstanding
      }
    }
  }
`;

const WEEKLY_TREND_ALL_QUERY = `
  query TeamWeeklyTrend($first: Int, $filter: WeeklyCollectionTrendFilterInput) {
    dashboard {
      weeklyCollectionTrendList(first: $first, filter: $filter) {
        edges {
          node {
            creditControllerId
            collectedAmount
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

// --- Helpers ---

async function fetchCaseTotals(
  apiConfig: Parameters<typeof graphqlQuery>[0],
  filter: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<{ count: number; outstanding: number; displayOutstanding: number }> {
  const response = await graphqlQuery<CaseTotalsResponse>(apiConfig, CASE_TOTALS_QUERY, { filter }, signal);
  const totals = response.collectionCases.collectionCaseTotals;
  return {
    count: totals.totalCount,
    outstanding: totals.totalOutstanding,
    displayOutstanding: totals.totalDisplayOutstanding,
  };
}

// --- Mock data ---

function generateMockTeamOverview(): TeamMemberOverview[] {
  return [
    {
      userId: 1,
      name: 'Sarah Johnson',
      initials: 'SJ',
      avatarColor: '#3b82f6',
      activeCases: 12,
      collected: 45000,
      outstanding: 78000,
      closed: 5,
    },
    {
      userId: 2,
      name: 'Marcus Clarke',
      initials: 'MC',
      avatarColor: '#22c55e',
      activeCases: 8,
      collected: 32000,
      outstanding: 54000,
      closed: 3,
    },
    {
      userId: 3,
      name: 'Emily Rodriguez',
      initials: 'ER',
      avatarColor: '#f97316',
      activeCases: 15,
      collected: 28000,
      outstanding: 92000,
      closed: 7,
    },
  ];
}

// --- Hook ---

export function useTeamOverviewData(dateFrom?: Date, dateTo?: Date, currencyCode?: string) {
  const apiConfig = useApiConfig();
  const { data: users } = useDashboardFilterUsers();
  const mockData = generateMockTeamOverview();

  return useModeAwareQuery<TeamMemberOverview[], Error>(
    [
      'team-overview-data',
      dateFrom?.toISOString(),
      dateTo?.toISOString(),
      currencyCode,
      users?.map((x) => x.id).join(','),
    ],
    async ({ signal }) => {
      if (!users || users.length === 0) return [];

      const effectiveFrom = dateFrom ?? new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
      const effectiveTo = dateTo ?? new Date();

      // 1. Fetch weekly collection trend for all users in the date range
      const trendFilter: Record<string, unknown> = {
        weekStartDate: {
          ge: effectiveFrom.toISOString().split('T')[0],
          le: effectiveTo.toISOString().split('T')[0],
        },
      };

      if (currencyCode) {
        trendFilter.currencyCode = { eq: currencyCode };
      }

      const trendResponse = await graphqlQuery<WeeklyTrendResponse>(
        apiConfig,
        WEEKLY_TREND_ALL_QUERY,
        { first: 1000, filter: trendFilter },
        signal,
      );

      // Group collected by creditControllerId
      const collectedByUser = new Map<number, number>();
      for (const edge of trendResponse.dashboard.weeklyCollectionTrendList.edges) {
        const node = edge.node;
        if (node.creditControllerId !== null) {
          const current = collectedByUser.get(node.creditControllerId) ?? 0;
          collectedByUser.set(node.creditControllerId, current + node.collectedAmount);
        }
      }

      // 2. For each user, fetch active case count, closed count, and outstanding
      const members = await Promise.all(
        users.map(async (user) => {
          const userId = Number(user.id);
          const userFilter = { assignedToUserId: { eq: userId } };

          const currencyFilter = currencyCode ? { currencyCode: { eq: currencyCode } } : {};

          const [activeTotals, closedTotals] = await Promise.all([
            fetchCaseTotals(apiConfig, { ...userFilter, ...currencyFilter, status: { eq: 'Active' } }, signal),
            fetchCaseTotals(apiConfig, { ...userFilter, ...currencyFilter, status: { eq: 'Closed' } }, signal),
          ]);

          return {
            userId,
            name: user.name,
            initials: user.initials ?? '',
            avatarColor: user.avatarColor ?? '#6b7280',
            activeCases: activeTotals.count,
            collected: collectedByUser.get(userId) ?? 0,
            outstanding: currencyCode ? activeTotals.displayOutstanding : activeTotals.outstanding,
            closed: closedTotals.count,
          };
        }),
      );

      return members;
    },
    mockData,
    {
      staleTime: 30_000,
      refetchOnMount: 'always',
      enabled: (users?.length ?? 0) > 0,
    },
  );
}
